import React, { useState, useEffect } from "react";
import ExaminationSidebar from "../../components/sidebar/ExaminationSidebar";
import ExaminationNavbar from "../../components/navbar/ExaminationNavbar.jsx";
import "./overallResult.scss";
import LineCharts from "../../components/chart/LineChart";
import CustomPieChart from "../../components/chart/PieChart.jsx";
import StackedBarChart from "../../components/chart/StackedBarChart.jsx";
import Select from "react-select";
import { serverUrl } from "../../temp.js";
import ExaminationCard from "../../components/card/ExaminationCard.jsx";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import SubjectBarChart from "../../components/chart/SubjectBarChart.jsx";
import { StackedBarChartType } from "../../enum.js";
import { formatMonths } from "../../generalServices.js";
import OverallGradeBarChart from "../../components/chart/OverallGradeBarChart.jsx";

const OverallResult = () => {
  const centerId = localStorage.getItem("center_id") || 38;
  const [token] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(
    localStorage.getItem("sidebar") || false
  );
  const [selectedClassId, setSelectedClassId] = useState(() => {
    const stored = localStorage.getItem("class-id");
    return stored ? stored : "7"; // Default to class ID 7 if nothing stored
  });

  // Dropdown states
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);

  // Options states
  const [monthOptions, setMonthOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);

  // Chart data states
  const [lineChartData, setLineChartData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [stackedBarChartData, setStackedBarChartData] = useState([]);
  const [subjectKeys, setSubjectKeys] = useState([]);
  const [overallGradeBarChartData, setOverallGradeBarChartData] = useState([]); // New state
  const [loadingOverallGradeBarChart, setLoadingOverallGradeBarChart] =
    useState(false); // New loading state

  const idealGrades = ["A1", "A", "B", "C", "D", "Retry"];
  const idealColors = [
    "#00C49F",
    "#0088FE",
    "#FFBB28",
    "#FF8042",
    "#AF19FF",
    "#FF193D",
  ];

  const [cardData, setCardData] = useState({
    course_covered: 0,
    teacher_attendance: 0,
    targeted_course_covered: 0,
  });

  const [subjectBarChartData, setSubjectBarChartData] = useState([]);

  // Loading states
  const [loadingSubjectBarChart, setLoadingSubjectBarChart] = useState(false);
  const [loading, setLoading] = useState({
    months: true,
    subjects: true,
    teachers: true,
    students: true,
    lineChart: true,
    pieChart: true,
    stackedBarChart: true,
    cards: true,
  });

  // Function to calculate grade based on percentage
  const getGradeFromPercentage = (percentage) => {
    if (percentage >= 79) return "A1";
    if (percentage >= 69) return "A";
    if (percentage >= 59) return "B";
    if (percentage >= 49) return "C";
    return "Retry";
  };

  const redirectToLogin = () => {
    alert("Please Login first then you can access this page...");
    window.location.href = "/";
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    localStorage.setItem("sidebar", !sidebarOpen);
  };

  const handleClassChange = (newClassId) => {
    setSelectedClassId(newClassId);
    localStorage.setItem("class-id", newClassId);

    // Reset filters when class changes
    setSelectedMonths([]);
    setSelectedSubjects([]);
    setSelectedTeachers([]);
    setSelectedStudents([]);
  };

  // Fetch month options
  useEffect(() => {
    fetch(
      `${serverUrl}/result_dashboard/month_dropdown?centerId=${centerId}&classId=${selectedClassId}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setMonthOptions(
            data.data.map((month) => ({
              value: month.month,
              label: month.month_name,
            }))
          );
        }
        setLoading((prev) => ({ ...prev, months: false }));
      })
      .catch((error) => {
        console.error("Error fetching months:", error);
        setLoading((prev) => ({ ...prev, months: false }));
      });
  }, [selectedClassId]);

  // Fetch subject options when class changes
  useEffect(() => {
    if (!selectedClassId) return;

    setLoading((prev) => ({ ...prev, subjects: true }));

    fetch(
      `${serverUrl}/result_dashboard/subject_dropdown?centerId=${centerId}&classId=${selectedClassId}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          const filteredSubjects = data.data
            .filter(
              (subject) =>
                subject.subject_name !== "Atp-English" &&
                subject.subject_name !== "Atp-Maths"
            )
            .map((subject) => ({
              value: subject.subject_id,
              label: subject.subject_name,
            }));

          setSubjectOptions(filteredSubjects);
        }

        setLoading((prev) => ({ ...prev, subjects: false }));
      })
      .catch((error) => {
        console.error("Error fetching subjects:", error);
        setLoading((prev) => ({ ...prev, subjects: false }));
      });
  }, [selectedClassId]);

  // Fetch teacher options when subjects change
  useEffect(() => {
    if (!selectedClassId) {
      setTeacherOptions([]);
      setSelectedTeachers([]);
      return;
    }

    setLoading((prev) => ({ ...prev, teachers: true }));
    const subjectIds = selectedSubjects.map((sub) => sub.value);

    fetch(
      `${serverUrl}/result_dashboard/teacher_dropdown?centerId=${centerId}&classId=${selectedClassId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ subjectId: subjectIds }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setTeacherOptions(
            data.data.map((teacher) => ({
              value: teacher.id,
              label: teacher.teacher_name,
            }))
          );
        }
        setLoading((prev) => ({ ...prev, teachers: false }));
      })
      .catch((error) => {
        console.error("Error fetching teachers:", error);
        setLoading((prev) => ({ ...prev, teachers: false }));
      });
  }, [selectedClassId, selectedSubjects]);

  // Fetch student options when class changes
  useEffect(() => {
    if (!selectedClassId) return;

    setLoading((prev) => ({ ...prev, students: true }));
    fetch(
      `${serverUrl}/result_dashboard/student_dropdown?centerId=${centerId}&classId=${selectedClassId}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setStudentOptions(
            data.data.map((student) => ({
              value: student.id,
              label: student.name,
            }))
          );
        }
        setLoading((prev) => ({ ...prev, students: false }));
      })
      .catch((error) => {
        console.error("Error fetching students:", error);
        setLoading((prev) => ({ ...prev, students: false }));
      });
  }, [selectedClassId]);

  useEffect(() => {
    if (!selectedClassId) return;

    setLoading((prev) => ({ ...prev, cards: true }));
    const monthIds = selectedMonths.map((m) => m.value);
    const subjectIds = selectedSubjects.map((s) => s.value);
    const teacherIds = selectedTeachers.map((t) => t.value);
    const studentIds = selectedStudents.map((s) => s.value);

    fetch(
      `${serverUrl}/result_dashboard/home_card_data?centerId=${centerId}&classId=${selectedClassId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          months: monthIds,
          subjects: subjectIds,
          teachers: teacherIds,
          students: studentIds,
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setCardData(data.data);
        }
        setLoading((prev) => ({ ...prev, cards: false }));
      })
      .catch((error) => {
        console.error("Error fetching card data:", error);
        setLoading((prev) => ({ ...prev, cards: false }));
      });
  }, [
    selectedClassId,
    selectedMonths,
    selectedSubjects,
    selectedTeachers,
    selectedStudents,
  ]);

  // Fetch line chart data (class average)
  useEffect(() => {
    if (!selectedClassId) return;

    setLoading((prev) => ({ ...prev, lineChart: true }));
    const monthIds = selectedMonths.map((m) => m.value);
    const subjectIds = selectedSubjects.map((s) => s.value);

    fetch(
      `${serverUrl}/result_dashboard/chart/class-average?centerId=${centerId}&classId=${selectedClassId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          monthids: monthIds,
          subjectids: subjectIds,
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setLineChartData(data.data);
        }
        setLoading((prev) => ({ ...prev, lineChart: false }));
      })
      .catch((error) => {
        console.error("Error fetching line chart data:", error);
        setLoading((prev) => ({ ...prev, lineChart: false }));
      });
  }, [selectedClassId, selectedMonths, selectedSubjects]);

  // Fetch pie chart data
  useEffect(() => {
    if (!selectedClassId) return;

    setLoading((prev) => ({ ...prev, pieChart: true }));
    const monthIds = selectedMonths.map((m) => m.value);
    const studentIds = selectedStudents.map((s) => s.value);
    const subjectIds = selectedSubjects.map((s) => s.value);

    fetch(
      `${serverUrl}/result_dashboard/pie-chart?centerId=${centerId}&classId=${selectedClassId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          monthids: monthIds,
          studentids: studentIds,
          subjectids: subjectIds,
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          // // Transform API data to pie chart format
          // const idealGrades = ['A1', 'A', 'B','C', 'D', 'Retry']
          // const idealColors = ['#00C49F', '#0088FE', '#FFBB28', '#FF8042', '#AF19FF', '#FF193D']
          // const transformedData = Object.entries(data.data).map(([name, value]) => ({
          //     name,
          //     value
          // }));
          // console.log("transformedData", transformedData)
          // setPieChartData(transformedData);

          const transformedData = idealGrades.map((grade) => ({
            name: grade,
            value: data.data[grade] ?? 0, // Use 0 if grade is missing
          }));

          console.log("transformedData", transformedData);
          setPieChartData(transformedData);
        }
        setLoading((prev) => ({ ...prev, pieChart: false }));
      })
      .catch((error) => {
        console.error("Error fetching pie chart data:", error);
        setLoading((prev) => ({ ...prev, pieChart: false }));
      });
  }, [selectedClassId, selectedMonths, selectedStudents, selectedSubjects]);

  // Fetch stacked bar chart data
  useEffect(() => {
    if (!selectedClassId) return;

    setLoading((prev) => ({ ...prev, stackedBarChart: true }));

    const monthIds = selectedMonths.map((m) => m.value);
    const studentIds = selectedStudents.map((s) => s.value);
    // const subjectIds = selectedSubjects.map(s => s.value);

    fetch(
      `${serverUrl}/result_dashboard/overallresults?centerId=${centerId}&classId=${selectedClassId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          monthids: monthIds,
          studentids: studentIds,
          subjectids: [],
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          console.log("data.data overallresults", data.data);
          const { transformedData, subjects } = transformStackedBarData(
            data.data
          );
          console.log("transformedData", transformedData);

          // Sort by overall result in descending order
          const sortedData = [...transformedData].sort((a, b) => {
            return b.overall - a.overall;
          });

          console.log("sortedData", sortedData);

          setStackedBarChartData(sortedData);
          setSubjectKeys(subjects);
        }
        setLoading((prev) => ({ ...prev, stackedBarChart: false }));
      })
      .catch((error) => {
        console.error("Error fetching stacked bar chart data:", error);
        setLoading((prev) => ({ ...prev, stackedBarChart: false }));
      });
  }, [selectedClassId, selectedMonths, selectedStudents]);

  // Add new useEffect for subject-specific chart
  useEffect(() => {
    // Only fetch if exactly one subject is selected
    if (selectedSubjects.length !== 1) {
      setSubjectBarChartData([]);
      return;
    }

    setLoadingSubjectBarChart(true);

    const subjectId = selectedSubjects[0].value;
    const monthIds = selectedMonths.map((m) => m.value);
    const studentIds = selectedStudents.map((s) => s.value);

    fetch(
      `${serverUrl}/result_dashboard/subjectresult?centerId=${centerId}&classId=${selectedClassId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          monthids: monthIds,
          studentids: studentIds,
          subjectids: [subjectId],
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          // Transform API data to chart format
          const transformedData = data.data.students.map((student) => {
            const subjectKey = Object.keys(student.subjects)[0];
            const subjectData = student.subjects[subjectKey];
            return {
              name: student.common_name,
              month: formatMonths(student.month),
              percentage: parseFloat(subjectData.percentage),
              // average: parseFloat(subjectData.percentage),
              grade: subjectData.grade,
            };
          });

          // Sort by percentage in descending order
          transformedData.sort((a, b) => b.percentage - a.percentage);
          setSubjectBarChartData(transformedData);
        }
        setLoadingSubjectBarChart(false);
      })
      .catch((error) => {
        console.error("Error fetching subject result data:", error);
        setLoadingSubjectBarChart(false);
      });
  }, [selectedClassId, selectedMonths, selectedStudents, selectedSubjects]);

  // Fetch overall grade bar chart data
  useEffect(() => {
    if (selectedMonths.length !== 1) {
      setOverallGradeBarChartData([]);
      return;
    }

    setLoadingOverallGradeBarChart(true);

    const monthId = selectedMonths[0].value;
    const studentIds = selectedStudents.map((s) => s.value);

    fetch(
      `${serverUrl}/result_dashboard/overallresults?centerId=${centerId}&classId=${selectedClassId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          monthids: [monthId],
          studentids: studentIds,
          subjectids: [],
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          const transformedData = data.data.map((record) => {
            // Only check subjects that the student actually has
            const subjectPercentages = subjectOptions
              .map((sub) => record[sub.label]) // student value if exists
              .filter(
                (v) => v !== undefined && v !== null && v !== "" // ignore missing subjects
              )
              .map((v) => parseFloat(v)); // convert to number

            // Check if any real subject is below passing
            const hasFailedSubject = subjectPercentages.some((p) => p < 50);

            // Overall percentage from API
            const overallPercentage = parseFloat(
              record["over all result"] || 0
            );

            return {
              name: record.common_name,
              month: formatMonths(record.month),
              percentage: overallPercentage,
              grade: hasFailedSubject
                ? "Retry"
                : getGradeFromPercentage(overallPercentage),
            };
          });

          // Sort descending by overall percentage
          transformedData.sort((a, b) => b.percentage - a.percentage);
          setOverallGradeBarChartData(transformedData);
        }

        setLoadingOverallGradeBarChart(false);
      })
      .catch((error) => {
        console.error("Error fetching overall grade bar chart data:", error);
        setLoadingOverallGradeBarChart(false);
      });
  }, [selectedClassId, selectedMonths, selectedStudents, subjectOptions]);

  // Helper to transform stacked bar chart data
  const transformStackedBarData = (apiData) => {
    // Get unique subjects from all records
    const subjectSet = new Set();
    apiData.forEach((record) => {
      Object.keys(record).forEach((key) => {
        // Exclude non-subject fields
        if (
          !["month", "name", "common_name", "over all result"].includes(
            key.trim()
          )
        ) {
          subjectSet.add(key.trim());
        }
      });
    });

    const subjects = Array.from(subjectSet);

    // Group data by student common_name
    const groupedData = {};
    apiData.forEach((record) => {
      const studentName = record.common_name; // ✅ Using common_name now
      if (!groupedData[studentName]) {
        groupedData[studentName] = {
          scores: {},
          overall: 0,
        };
      }

      // Process subject scores
      subjects.forEach((subject) => {
        const subjectKey = subject.trim();
        if (record[subjectKey]) {
          if (!groupedData[studentName].scores[subjectKey]) {
            groupedData[studentName].scores[subjectKey] = [];
          }
          groupedData[studentName].scores[subjectKey].push(
            parseFloat(record[subjectKey])
          );
        }
      });

      // Store overall result from API
      if (record["over all result"]) {
        groupedData[studentName].overall = parseFloat(
          record["over all result"]
        );
      }
    });

    // Create transformed data array
    const transformedData = Object.keys(groupedData).map((studentName) => {
      const studentData = {
        name: studentName,
        overall: groupedData[studentName].overall,
      };
      subjects.forEach((subject) => {
        const subjectKey = subject.trim();
        if (groupedData[studentName].scores[subjectKey]) {
          // Calculate average score for the subject
          const sum = groupedData[studentName].scores[subjectKey].reduce(
            (a, b) => a + b,
            0
          );
          studentData[subjectKey] =
            sum / groupedData[studentName].scores[subjectKey].length;
        }
      });
      return studentData;
    });

    return { transformedData, subjects };
  };

  // Helper to format applied filters
  const formatAppliedFilters = (filters) => {
    return filters
      .filter((filter) => filter.value.length > 0)
      .map((filter) => ({
        label: filter.label,
        value: filter.value.map((v) => v.label).join(", "),
      }));
  };

  // Get applied filters for cards
  const cardFilters = formatAppliedFilters([
    { label: "Month", value: selectedMonths },
    { label: "Subject", value: selectedSubjects },
    { label: "Teacher", value: selectedTeachers },
    { label: "Student", value: selectedStudents },
  ]);

  // Filters for charts
  const chartFilters = formatAppliedFilters([
    { label: "Month", value: selectedMonths },
    { label: "Subject", value: selectedSubjects },
  ]);

  // Filters for pie charts
  const pieFilters = formatAppliedFilters([
    { label: "Month", value: selectedMonths },
    { label: "Subject", value: selectedSubjects },
    { label: "Student", value: selectedStudents },
  ]);

  const stackedBarFilters = formatAppliedFilters([
    { label: "Month", value: selectedMonths },
    { label: "Student", value: selectedStudents },
    // { label: "Subject", value: selectedSubjects }
  ]);

  // Add applied filters for subject bar chart
  const subjectBarChartFilters = formatAppliedFilters([
    { label: "Month", value: selectedMonths },
    { label: "Student", value: selectedStudents },
  ]);

  const overallGradeBarChartFilters = formatAppliedFilters([
    { label: "Month", value: selectedMonths },
    { label: "Student", value: selectedStudents },
  ]);

  return (
    <>
      {!token && redirectToLogin()}
      {token && (
        <div className="overallResult">
          {sidebarOpen && (
            <ExaminationSidebar isOpen={sidebarOpen} toggle={toggleSidebar} />
          )}
          <div className="homeContainer">
            <ExaminationNavbar
              isOpen={sidebarOpen}
              toggle={toggleSidebar}
              selectedClassId={selectedClassId}
              onClassChange={handleClassChange}
            />
            <div className="heading">
              <h2>Overall Result</h2>
            </div>
            <div className="widgets">
              <div className="dropdown-container">
                <label htmlFor="dropdown1">Month</label>
                <Select
                  id="dropdown1"
                  isMulti
                  options={monthOptions}
                  value={selectedMonths}
                  onChange={setSelectedMonths}
                  isLoading={loading.months}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
              <div className="dropdown-container">
                <label htmlFor="dropdown2">Subject</label>
                <Select
                  id="dropdown2"
                  isMulti
                  options={subjectOptions}
                  value={selectedSubjects}
                  onChange={setSelectedSubjects}
                  isLoading={loading.subjects}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
              <div className="dropdown-container">
                <label htmlFor="dropdown3">Teacher</label>
                <Select
                  id="dropdown3"
                  isMulti
                  options={teacherOptions}
                  value={selectedTeachers}
                  onChange={setSelectedTeachers}
                  isLoading={loading.teachers}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
              <div className="dropdown-container">
                <label htmlFor="dropdown4">Student</label>
                <Select
                  id="dropdown4"
                  isMulti
                  options={studentOptions}
                  value={selectedStudents}
                  onChange={setSelectedStudents}
                  isLoading={loading.students}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
            </div>
            <div className="widgets">
              <ExaminationCard
                type={"Teacher Attendance"}
                value={`${parseFloat(cardData.teacher_attendance || 0).toFixed(
                  2
                )}%`}
                icon={<PersonOutlinedIcon />}
                loading={loading.cards}
                appliedFilters={cardFilters}
              />
              <ExaminationCard
                type={"Targeted Course"}
                value={`${parseFloat(
                  cardData.targeted_course_covered || 0
                ).toFixed(2)}%`}
                icon={<PersonOutlinedIcon />}
                loading={loading.cards}
                appliedFilters={cardFilters}
              />
              <ExaminationCard
                type={"Course Covered"}
                value={`${parseFloat(cardData.course_covered || 0).toFixed(
                  2
                )}%`}
                icon={<PersonOutlinedIcon />}
                loading={loading.cards}
                appliedFilters={cardFilters}
              />
            </div>
            <div className="widgets">
              <CustomPieChart
                data={pieChartData}
                colors={idealColors}
                title="Grades"
                loading={loading.pieChart}
                appliedFilters={pieFilters}
              />
              <LineCharts
                title="Class Average"
                data={lineChartData.map((item) => item.average_marks)}
                labels={lineChartData.map((item) => item.month_name)}
                loading={loading.lineChart}
                appliedFilters={chartFilters}
              />
            </div>
            <div className="charts">
              <StackedBarChart
                title={"Overall Result"}
                data={stackedBarChartData}
                subjectKeys={subjectKeys}
                loading={loading.stackedBarChart}
                appliedFilters={stackedBarFilters}
                chartType={StackedBarChartType.RESULTS}
              />
            </div>
            {selectedMonths.length === 1 && (
              <div className="charts">
                <OverallGradeBarChart
                  title={`${selectedMonths[0].label} Overall Performance by Grade`}
                  data={overallGradeBarChartData}
                  loading={loadingOverallGradeBarChart}
                  appliedFilters={overallGradeBarChartFilters}
                />
              </div>
            )}
            {selectedMonths.length === 1 && selectedSubjects.length === 1 && (
              <div className="charts">
                <SubjectBarChart
                  title={`${selectedSubjects[0].label} Performance`}
                  data={subjectBarChartData}
                  loading={loadingSubjectBarChart}
                  appliedFilters={subjectBarChartFilters}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default OverallResult;
